import { useEffect, useState, type ReactNode } from 'react';
import { Button, Flex, Text, Callout } from '@dodilio/ui-design-lib';
import { useParams } from 'react-router-dom';
import { auth, restoreSession, signOut } from '../../auth';
import { whoFromToken } from '../../session';
import { AcceptInviteLayout } from './layout';
import { AcceptInviteForm } from './form';
import { AcceptAsExistingUser } from './accept-existing';
import { ServiceRegister } from '../../api';
import { GetInviteResponse } from '../../types/register';
import { isTransient } from '../../apiError';

/**
 * An invite link has to render one of three things, and only the server can
 * say which:
 *
 *   no session  -> create an account (today's flow), or sign in to accept
 *   matches     -> you are the invited user: Join
 *   mismatch    -> you are signed in as somebody else
 *
 * `GetInvite` classifies the CALLER'S session. It never reports whether the
 * invited address already has an account — an invite code arrives by email, so
 * answering that would let anyone holding one discover whether an address is
 * registered. A logged-out visitor is offered both doors and the server decides
 * which opens at accept time.
 */
export const AcceptInviteAccount = () => {
    const { code } = useParams<{ code: string }>();
    const [invite, setInvite] = useState<GetInviteResponse | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [token, setToken] = useState<string | undefined>();
    const [retry, setRetry] = useState(false);

    useEffect(() => {
        if (!code) {
            setFailed('This invite link is missing its code.');
            return;
        }
        let cancelled = false;
        (async () => {
            // Pick up an existing session WITHOUT forcing a login: a brand-new
            // invitee has no account yet, so redirecting them to Keycloak here
            // would strand them. `authenticate()` is non-interactive.
            let bearer: string | undefined;
            try {
                if ((await restoreSession()) === 'signed-in') bearer = auth.getAccessToken();
            } catch {
                // No session is a perfectly normal state for this page.
            }
            if (cancelled) return;
            setToken(bearer);

            try {
                const r = await ServiceRegister.getInvite(code, bearer);
                if (!cancelled) setInvite(r);
            } catch (e) {
                if (cancelled) return;
                // Down or unreachable says nothing about the invitation: never call it dead.
                if (isTransient(e)) { setRetry(true); setFailed('Dodil couldn\'t be reached just now. Try again in a moment.'); return; }
                // A spent, expired or unknown code all answer the same way, by
                // design: the page must not tell a code-holder which it was.
                setFailed('This invitation is no longer valid. Ask whoever invited you to send a new one.');
            }
        })().catch((e) => {
            console.warn('[register] invitation', e);
            if (!cancelled) { setRetry(true); setFailed('That didn\'t work. Try again.'); }
        });
        return () => {
            cancelled = true;
        };
    }, [code]);

    let body: ReactNode;
    if (failed) {
        body = (
            <Flex direction="column" gap="3">
                <Callout.Root color="red" size="2" role="alert">
                    <Callout.Text>{failed}</Callout.Text>
                </Callout.Root>
                {/* A Keycloak instance starts once per page: trying again is a reload. */}
                {retry && <Button radius="full" size="2" variant="soft" onClick={() => window.location.reload()}>Try again</Button>}
            </Flex>
        );
    } else if (!invite) {
        body = <Text size="2">Checking your invitation…</Text>;
    } else if (invite.status === 'INVITE_SESSION_STATUS_MATCHES' && token) {
        body = <AcceptAsExistingUser code={code!} invite={invite} token={token} />;
    } else if (invite.status === 'INVITE_SESSION_STATUS_MISMATCH') {
        // Signed in as someone the invitation isn't for. iam joins only the invited
        // address, so the way through is to switch: out, then back to this invitation.
        const current = whoFromToken(token)?.email;
        const orgName = invite.organizationDisplayName || invite.organizationName;
        body = (
            <Flex direction="column" gap="3">
                <Text size="2">
                    The invitation to join <strong>{orgName}</strong> is for{' '}
                    <strong>{invite.invitedEmail || 'another email address'}</strong>
                    {current ? <>, and you&apos;re signed in as <strong>{current}</strong></> : <>, not the account you&apos;re signed in with</>}.
                </Text>
                <Button radius="full" size="2" variant="classic" onClick={() => { void signOut(window.location.href); }}>
                    {invite.invitedEmail ? `Switch to ${invite.invitedEmail}` : 'Sign out and switch account'}
                </Button>
                <Text size="1" color="gray">You&apos;ll come back to this invitation to sign in or create the invited account.</Text>
            </Flex>
        );
    } else {
        body = <AcceptInviteForm organizationName={invite.organizationDisplayName || invite.organizationName} />;
    }

    return <AcceptInviteLayout>{body}</AcceptInviteLayout>;
};
